/**
 * AR Email Intelligence Demo — scripted Nami beats (E0 / GEN-2262).
 *
 * THROWAWAY DEMO CODE. The seven beats for the email-intelligence storyline,
 * kept in a SEPARATE array from `AR_DEMO_SCRIPTS` to minimize merge conflicts as
 * E1–E4 fan out. `demo-script.ts` concatenates `AR_INTEL_SCRIPTS` into the
 * matched set (one small, clearly-commented seam), so these match through the
 * exact same `matchArDemoScript` machinery and fire the same way.
 *
 * Each beat's `toolNames` reference tools that the E1–E4 pages will register via
 * `useArAgentPage` (or directly with the registry). A tool that isn't registered
 * yet is skipped with a console warn, so unbuilt beats degrade gracefully.
 *
 * Tool-name ownership (E1–E4 must register these EXACT names):
 *  - S0 `intel_show_corpus_learning`     — E1
 *  - S1 `intel_show_profiles`            — E2
 *  - S2 `intel_show_voice_draft` (hero)  — E2
 *  - S3 `intel_show_tone_variants`       — E2
 *  - S4 `intel_show_customer_intelligence` — E3
 *  - S5 `intel_apply_recommendation`     — E3
 *  - S6 `intel_send_email`, `intel_show_reply` — E4
 *
 * Page-id scheme for the surfaces: `ar-intel-*` (e.g. `ar-intel-corpus`,
 * `ar-intel-voice`, `ar-intel-intelligence`, `ar-intel-send`).
 */

import type { ArDemoScript } from '../demo-script';

export const AR_INTEL_SCRIPTS: ArDemoScript[] = [
  {
    scene: 'Intel S0 — what the agent learned from the inbox',
    prompts: [
      'sarah show me what you learned from my inbox',
      'show me what you learned from my inbox',
      'what did you learn from my inbox',
      'what you learned from my inbox',
    ],
    intent: [
      ['learn', 'learned', 'mined', 'mine', 'scanned', 'read', 'analyzed'],
      ['inbox', 'email', 'emails', 'corpus', 'mailbox', 'mail'],
    ],
    narration:
      "I read through your inbox — 82,497 emails across 9,643 threads with 87 customers, going back to 2020. From your sent mail I extracted how you write, and from each customer's history I built a profile of how they actually pay. Here's what I found.",
    toolNames: ['intel_show_corpus_learning'],
    narrateAfterTools: true,
  },
  {
    scene: 'Intel S1 — voice profile',
    prompts: [
      'show me my voice profile',
      'what did you learn about how i write',
      'how do i write',
      'show my writing style',
    ],
    intent: [
      ['voice', 'writing', 'write', 'style', 'tone', 'sound'],
      ['profile', 'learn', 'learned', 'mine', 'me', 'my', 'how'],
    ],
    narration:
      'Here\'s your voice profile, Sarah. You\'re warm but firm — you open on first names, keep paragraphs short, lead with rapport before the ask, and sign off "Best, Sarah". I pulled your characteristic phrases straight from your sent mail.',
    toolNames: ['intel_show_profiles'],
  },
  {
    scene: 'Intel S2 (hero) — voice-matched draft for Crestline',
    prompts: [
      'draft a nudge for crestline overdue invoice',
      'draft a nudge for crestlines overdue invoice',
      'draft a nudge for crestline',
      'draft an overdue nudge for crestline',
    ],
    intent: [
      ['draft', 'write', 'compose', 'nudge', 'reminder', 'email'],
      ['crestline', 'overdue', 'invoice'],
    ],
    narration:
      "Here's the nudge for Crestline. On the left is a generic AI draft; on the right is the one written in your voice — it opens on Maria by name, references your prior rapport, and stays warm but firm. Same facts, but it sounds like you.",
    toolNames: ['intel_show_voice_draft'],
  },
  {
    scene: 'Intel S3 — tone variants for Granite Peak & Cascade',
    prompts: [
      'show me how the tone changes for granite peak and cascade',
      'how does the tone change for granite peak and cascade',
      'show the tone variants for granite peak and cascade',
      'show tone variants',
    ],
    intent: [
      ['tone', 'tones', 'firm', 'soft', 'variant', 'variants', 'style'],
      ['granite', 'cascade', 'change', 'changes', 'compare'],
    ],
    narration:
      'The tone adapts to the customer. For combative Granite Peak I lead firm with evidence; for loyal, deferential Cascade I keep it soft and relationship-first. Here are both, firm and soft side by side.',
    toolNames: ['intel_show_tone_variants'],
  },
  {
    scene: 'Intel S4 — customer intelligence for Granite Peak',
    prompts: [
      'what do we know about granite peak',
      'what do we know about granite peak distributors',
      'show me what we know about granite peak',
      'tell me about granite peak',
    ],
    intent: [
      ['know', 'knowledge', 'intelligence', 'learned', 'about', 'tell'],
      ['granite', 'peak', 'customer', 'distributors'],
    ],
    narration:
      "Here's everything I learned about Granite Peak from their email history. Maria releases payment — not the AP inbox. They always dispute freight, reply in 3–5 days, and honor their promises once you send a POD. Each fact links back to the email I learned it from.",
    toolNames: ['intel_show_customer_intelligence'],
  },
  {
    scene: 'Intel S5 — apply recommendations for Granite Peak',
    prompts: [
      'what should i do about granite peaks dispute',
      'what should i do about granite peak dispute',
      'apply your recommendations',
      'apply your recommendations for granite peak',
    ],
    intent: [
      [
        'should',
        'recommend',
        'recommendation',
        'recommendations',
        'apply',
        'do',
      ],
      ['granite', 'peak', 'dispute', 'freight'],
    ],
    narration:
      "Based on their history, two moves: pre-empt the freight dispute by attaching the signed POD up front, and CC Maria directly since she's the one who releases payment. I've applied both to the draft.",
    toolNames: ['intel_apply_recommendation'],
    narrateAfterTools: true,
  },
  {
    scene: 'Intel S6 — send the Granite Peak email + warm reply',
    prompts: [
      'send it',
      'send the email',
      'approve and send the granite peak email',
      'approve and send the email',
    ],
    intent: [
      ['send', 'approve', 'approved', 'fire', 'go'],
      ['it', 'email', 'granite', 'peak', 'draft', 'message'],
    ],
    narration:
      "Sent — with the POD attached and Maria CC'd. And she just replied: \"Thanks for sending the POD over so quickly — I'll get the remaining $8,640 onto Friday's check run.\" Exactly the outcome the history predicted.",
    toolNames: ['intel_send_email', 'intel_show_reply'],
    narrateAfterTools: true,
  },
];
